import React, { useState, useEffect } from 'react';
import './FilterModal.css';

const FilterModal = ({ isOpen, onClose, initialFilters, onApplyFilters }) => {
  const [filters, setFilters] = useState({
    service: initialFilters.service || '',
    location: initialFilters.location || '',
    gender: initialFilters.gender || '',
    minAge: initialFilters.minAge || 18,
    maxAge: initialFilters.maxAge || 100,
    minPrice: initialFilters.minPrice || 0,
    maxPrice: initialFilters.maxPrice || 2000,
    services: initialFilters.services || []
  });

  useEffect(() => {
    // Update filters when initialFilters change
    setFilters({
      service: initialFilters.service || '',
      location: initialFilters.location || '',
      gender: initialFilters.gender || '',
      minAge: initialFilters.minAge || 18,
      maxAge: initialFilters.maxAge || 100,
      minPrice: initialFilters.minPrice || 0,
      maxPrice: initialFilters.maxPrice || 2000,
      services: initialFilters.services || []
    });
  }, [initialFilters]);

  const serviceOptions = [
    { value: 'sweeping-mopping', label: '🧹 Sweeping & Mopping' },
    { value: 'cooking', label: '👨‍🍳 Cooking' },
    { value: 'laundry', label: '👕 Laundry' },
    { value: 'gardening', label: '🌱 Gardening' },
    { value: 'pet-care', label: '🐕 Pet Care' },
    { value: 'elderly-care', label: '👴 Elderly Care' },
    { value: 'babysitting', label: '👶 Babysitting' },
    { value: 'hair-dresser', label: '✂️ Hair Dresser' },
    { value: 'other', label: '🔧 Other' }
  ];

  const genderOptions = [
    { value: '', label: 'Any Gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceToggle = (service) => {
    setFilters(prev => {
      const updatedServices = prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];
      
      return {
        ...prev,
        services: updatedServices
      };
    });
  };

  const handleRangeChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters = {
      service: '',
      location: '',
      gender: '',
      minAge: 18,
      maxAge: 100,
      minPrice: 0,
      maxPrice: 2000,
      services: []
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay">
      <div className="filter-modal">
        <div className="filter-modal-header">
          <h2>Advanced Filters</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="filter-modal-content">
          <div className="filter-section">
            <h3>Basic Filters</h3>
            
            <div className="filter-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={filters.location}
                onChange={handleInputChange}
                placeholder="Enter city or area"
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="service">Main Service</label>
              <select
                id="service"
                name="service"
                value={filters.service}
                onChange={handleInputChange}
                className="filter-select"
              >
                <option value="">All Services</option>
                {serviceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filter-section">
            <h3>Helper Demographics</h3>
            
            <div className="filter-group">
              <label htmlFor="gender">Gender</label>
              <select
                id="gender"
                name="gender"
                value={filters.gender}
                onChange={handleInputChange}
                className="filter-select"
              >
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Age Range</label>
              <div className="range-slider-container">
                <div className="range-values">
                  <span>{filters.minAge}</span>
                  <span>{filters.maxAge}</span>
                </div>
                <div className="range-inputs">
                  <input
                    type="range"
                    name="minAge"
                    min="18"
                    max="100"
                    value={filters.minAge}
                    onChange={handleRangeChange}
                    className="range-slider"
                  />
                  <input
                    type="range"
                    name="maxAge"
                    min="18"
                    max="100"
                    value={filters.maxAge}
                    onChange={handleRangeChange}
                    className="range-slider"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="filter-section">
            <h3>Price Range (₹/hour)</h3>
            <div className="filter-group">
              <div className="range-slider-container">
                <div className="range-values">
                  <span>₹{filters.minPrice}</span>
                  <span>₹{filters.maxPrice}</span>
                </div>
                <div className="range-inputs">
                  <input
                    type="range"
                    name="minPrice"
                    min="0"
                    max="2000"
                    step="50"
                    value={filters.minPrice}
                    onChange={handleRangeChange}
                    className="range-slider"
                  />
                  <input
                    type="range"
                    name="maxPrice"
                    min="0"
                    max="2000"
                    step="50"
                    value={filters.maxPrice}
                    onChange={handleRangeChange}
                    className="range-slider"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="filter-section">
            <h3>Services Offered</h3>
            <div className="services-grid">
              {serviceOptions.map(service => (
                <label key={service.value} className="service-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.services.includes(service.value)}
                    onChange={() => handleServiceToggle(service.value)}
                  />
                  <span className="checkmark"></span>
                  {service.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        
        <div className="filter-modal-footer">
          <button className="reset-button" onClick={handleReset}>
            Reset All
          </button>
          <button className="apply-button" onClick={handleApply}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal; 